import { useState } from 'react'
import type { CharacterWithStats } from '@shared/types'
import { CONDITIONS_DATA } from '@shared/rules/data/conditions'
import { SPELLS_BY_ID } from '@shared/rules/data/spells'
import { formatModifier } from '@shared/rules/calc'
import { Badge } from '@/components/ui'
import { cn } from '@/lib/cn'

/**
 * One row per character with everything a DM asks for mid-session, and the two
 * actions that previously required opening a sheet: damage and healing.
 */
export function PartyStrip({
  characters, onPatch, onRoll,
}: {
  characters: CharacterWithStats[]
  onPatch: (id: string, patch: Record<string, unknown>) => void
  onRoll: (character: CharacterWithStats, formula: string, context: string) => void
}) {
  if (characters.length === 0) {
    return (
      <p className="rounded-[--radius-card] border border-dashed border-line px-6 py-10 text-center text-sm text-ink-500">
        No characters yet.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {characters.map((character) => (
        <PartyRow key={character.id} character={character} onPatch={onPatch} onRoll={onRoll} />
      ))}
    </div>
  )
}

function PartyRow({
  character, onPatch, onRoll,
}: {
  character: CharacterWithStats
  onPatch: (id: string, patch: Record<string, unknown>) => void
  onRoll: (character: CharacterWithStats, formula: string, context: string) => void
}) {
  const [amount, setAmount] = useState('')
  const [showConditions, setShowConditions] = useState(false)

  const { derived } = character
  const ratio = character.hpMax > 0 ? Math.max(0, character.hpCurrent / character.hpMax) : 0
  const down = character.hpCurrent <= 0
  const conditions = character.conditions ?? []

  const tone =
    down ? 'bg-ink-600'
    : ratio <= 0.25 ? 'bg-blood-500'
    : ratio <= 0.5 ? 'bg-gold-500'
    : 'bg-moss-500'

  function apply(sign: 1 | -1) {
    const value = Math.abs(Number(amount) || 0)
    if (!value) return
    if (sign === -1) {
      const fromTemp = Math.min(character.hpTemp, value)
      onPatch(character.id, {
        hpTemp: character.hpTemp - fromTemp,
        hpCurrent: Math.max(-99, character.hpCurrent - (value - fromTemp)),
      })
    } else {
      onPatch(character.id, { hpCurrent: Math.min(character.hpMax, character.hpCurrent + value) })
    }
    setAmount('')
  }

  function toggleCondition(name: string) {
    const next = conditions.includes(name)
      ? conditions.filter((c) => c !== name)
      : [...conditions, name]
    onPatch(character.id, { conditions: next })
  }

  const concentrating = character.concentratingOn ? SPELLS_BY_ID.get(character.concentratingOn) : null

  return (
    <div className={cn('panel p-3', down && 'border-blood-500/40')}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        {/* Identity */}
        <div className="flex min-w-[13rem] flex-[1.4] items-center gap-2.5">
          <span
            aria-hidden
            className={cn(
              'grid size-9 shrink-0 place-items-center rounded-xl text-xs font-semibold',
              down ? 'bg-blood-500/15 text-blood-500' : 'bg-fill text-ink-300',
            )}
          >
            {character.name.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium text-ink-100">{character.name}</p>
            <p className="truncate text-xs text-ink-500">
              {character.level} {character.class || 'adventurer'}
              {character.ownerName && ` · ${character.ownerName}`}
            </p>
          </div>
        </div>

        {/* The numbers a DM asks for by name */}
        <div className="flex shrink-0 items-center gap-3">
          <Vital label="AC" value={derived.armorClass} />
          <Vital label="Pass. Per" value={derived.passivePerception} highlight />
          <Vital label="Init" value={formatModifier(derived.initiative)} />
        </div>

        {/* Health with inline damage and healing */}
        <div className="flex min-w-[14rem] flex-[1.6] items-center gap-3">
          <div className="min-w-0 flex-1">
            <div className="mb-1 flex items-baseline justify-between gap-2">
              <span className={cn('font-mono text-sm tabular-nums', down ? 'text-blood-500' : 'text-ink-200')}>
                {character.hpCurrent}
                <span className="text-ink-500">/{character.hpMax}</span>
                {character.hpTemp > 0 && <span className="text-moss-500"> +{character.hpTemp}</span>}
              </span>
              {down && <span className="text-[10px] font-semibold uppercase tracking-wider text-blood-500">Down</span>}
            </div>
            <div className="h-1.5 w-full overflow-hidden rounded-full bg-track">
              <div className={cn('h-full rounded-full transition-[width] duration-300', tone)}
                style={{ width: `${Math.min(1, ratio) * 100}%` }} />
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1">
            <button type="button" onClick={() => apply(-1)} aria-label={`Damage ${character.name}`}
              className="pressable grid size-8 place-items-center rounded-lg bg-blood-500/12 text-blood-500 hover:bg-blood-500/25">
              −
            </button>
            <input
              value={amount}
              onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter') apply(e.shiftKey ? 1 : -1) }}
              onFocus={(e) => e.target.select()}
              inputMode="numeric"
              aria-label={`Hit point change for ${character.name}`}
              placeholder="0"
              className="h-8 w-12 rounded-lg border border-line bg-ink-900/60 text-center font-mono text-sm tabular-nums text-ink-100 focus:border-gold-500/60 focus:outline-none"
            />
            <button type="button" onClick={() => apply(1)} aria-label={`Heal ${character.name}`}
              className="pressable grid size-8 place-items-center rounded-lg bg-moss-500/12 text-moss-500 hover:bg-moss-500/25">
              +
            </button>
          </div>
        </div>

        {/* Secret checks, rolled by the DM without asking the player */}
        <div className="flex shrink-0 items-center gap-1">
          <RollChip label="Per" title="Secret Perception check"
            onClick={() => onRoll(character, `1d20${signed(derived.skills.perception)}`, `${character.name} — Perception (secret)`)} />
          <RollChip label="Ins" title="Secret Insight check"
            onClick={() => onRoll(character, `1d20${signed(derived.skills.insight)}`, `${character.name} — Insight (secret)`)} />
          <RollChip label="Sav" title="Secret saving throw (Wisdom)"
            onClick={() => onRoll(character, `1d20${signed(derived.saves.wis)}`, `${character.name} — Wisdom save (secret)`)} />
        </div>
      </div>

      {/* Status */}
      {(conditions.length > 0 || character.exhaustion > 0 || concentrating || showConditions) && (
        <div className="mt-2.5 flex flex-wrap items-center gap-1.5 border-t border-line pt-2.5">
          {concentrating && (
            <Badge tone="gold">Concentrating: {concentrating.name}</Badge>
          )}
          {character.exhaustion > 0 && <Badge tone="blood">Exhaustion {character.exhaustion}</Badge>}
          {conditions.map((name) => (
            <button key={name} type="button" onClick={() => toggleCondition(name)}
              title={`Remove ${name}`}
              className="pressable rounded-full border border-blood-500/25 bg-blood-500/12 px-2.5 py-0.5 text-[11px] text-blood-500 hover:bg-blood-500/25">
              {name} ✕
            </button>
          ))}
          {showConditions && (
            <div className="mt-1 flex w-full flex-wrap gap-1">
              {CONDITIONS_DATA.filter((c) => !conditions.includes(c.name)).map((c) => (
                <button key={c.id} type="button" onClick={() => { toggleCondition(c.name); setShowConditions(false) }}
                  title={c.summary}
                  className="pressable rounded-full border border-line px-2.5 py-0.5 text-[11px] text-ink-400 hover:border-line-strong hover:text-ink-200">
                  {c.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <button type="button" onClick={() => setShowConditions((v) => !v)}
        className="mt-2 text-[11px] text-ink-500 transition-colors hover:text-ink-300">
        {showConditions ? 'Close' : '+ condition'}
      </button>
    </div>
  )
}

const signed = (n: number): string => (n < 0 ? `${n}` : `+${n}`)

function Vital({ label, value, highlight }: { label: string; value: string | number; highlight?: boolean }) {
  return (
    <div className="text-center">
      <p className={cn('font-mono text-base leading-none tabular-nums', highlight ? 'text-gold-400' : 'text-ink-200')}>
        {value}
      </p>
      <p className="mt-1 text-[10px] uppercase tracking-wider text-ink-500">{label}</p>
    </div>
  )
}

function RollChip({ label, title, onClick }: { label: string; title: string; onClick: () => void }) {
  return (
    <button type="button" onClick={onClick} title={title}
      className="pressable rounded-lg border border-line px-2 py-1.5 text-[11px] text-ink-400 hover:border-gold-500/40 hover:text-gold-400">
      {label}
    </button>
  )
}
